import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { CountryDropdown } from '@/components/ui/CountryDropdown'
import { EmptyState } from '@/components/feedback/EmptyState'
import { Field } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { useTransportRefs } from '@/features/directories/api/useDirectory'
import {
  needsAcknowledgement,
  optionHoldsCosting,
  useCreateRouteOption,
  useDeleteRouteOption,
  useSelectRouteOption,
  useUpdateRouteOption,
} from '@/features/quotes/api/useRouteOptions'
import { cn } from '@/lib/cn'
import { placeName } from '@/lib/countries'
import { formatNumber } from '@/lib/format'
import { MODE_ICONS, modeFromCode, modeLabelKey } from '@/lib/icons'
import { Route } from 'lucide-react'
import type { QuoteDetail, RouteOption, RouteOptionPayload } from '@/types/domain'

/* Route options (Story 2.4, FR-3, FR-19).
 *
 * Up to three routings the client chooses between, exactly one selected. The
 * section owns the mutations and the sheet, like CargoSection, so a refusal
 * leaves the draft intact on screen.
 *
 * Two server refusals surface INSIDE this component rather than as toasts,
 * because both are conversations, not failures:
 *
 *  - Moving the selection off a costed quote answers 409 until acknowledged.
 *    The warning strip appears against the target option, names the
 *    consequence — the cost lines will price the new routing — and the
 *    confirm retries with the acknowledgement. The server enforces the
 *    confirmation; this UI is its voice, not its origin.
 *
 *  - Deleting the selected option while cost lines exist is refused outright:
 *    another option must be SELECTED first, so a quote is never left priced
 *    against nothing. The refusal is shown where the delete was pressed.
 *
 * The maximum of three is the server's (and the schema's); the Add affordance
 * disappears at three rather than disabling — a button that can never become
 * pressable again on this record is clutter, not information.
 */

const MAX_OPTIONS = 3

/** The letter a slot prints as on the offer: 1 is "A". */
function optionLetter(position: number): string {
  return String.fromCharCode(64 + position)
}

/** The letter the NEXT option would print as, for the dashed add tile.
 *
 * Off the highest position in use rather than off the count. Deleting the middle
 * of three leaves positions 1 and 3, and a count-based guess would offer to add
 * "C" for a slot that already exists. This is a label on a button, not a claim
 * the server is bound by — it mints the position itself — so it only has to stop
 * being obviously wrong in front of somebody who can see the tiles beside it. */
function nextOptionLetter(options: readonly RouteOption[]): string {
  const highest = options.reduce(
    (top, option) => Math.max(top, option.position),
    0,
  )
  return optionLetter(highest + 1)
}

export function RouteOptionsCard({ record }: { record: QuoteDetail }) {
  const { t } = useTranslation()
  const options = record.routeOptions ?? []
  const readOnly = record.status !== 'draft'

  const create = useCreateRouteOption(record.id)
  const update = useUpdateRouteOption(record.id)
  const select = useSelectRouteOption(record.id)
  const remove = useDeleteRouteOption(record.id)

  const [editing, setEditing] = useState<RouteOption | 'new' | null>(null)
  /** The option a selection move is waiting on the user to confirm. */
  const [confirmingSelect, setConfirmingSelect] = useState<string | null>(null)

  const pending =
    create.isPending || update.isPending || select.isPending || remove.isPending

  const handleSelect = (option: RouteOption, acknowledged: boolean) => {
    select.mutate(
      { optionId: option.id, acknowledged },
      {
        onSuccess: () => setConfirmingSelect(null),
        onError: (error) => {
          // The ordinary first half of the confirm flow (FR-3): show the
          // warning against this option and wait for the second press.
          if (needsAcknowledgement(error)) setConfirmingSelect(option.id)
        },
      },
    )
  }

  return (
    <Card>
      <CardHeader
        action={
          !readOnly && options.length < MAX_OPTIONS ? (
            <Button
              variant="secondary"
              size="sm"
              iconLeft={Plus}
              onClick={() => setEditing('new')}
            >
              {t('quotes.routes.add')}
            </Button>
          ) : undefined
        }
      >
        <CardTitle>{t('quotes.routes.title')}</CardTitle>
      </CardHeader>

      {options.length === 0 ? (
        <EmptyState
          icon={Route}
          title={t('quotes.routes.emptyTitle')}
          description={t('quotes.routes.emptyDescription')}
        />
      ) : (
        /* Tiles in a grid, not rows in a list. Up to three routings are things
           a client COMPARES — the offer prints them as columns A, B, C — and a
           stack of full-width rows is the one shape that cannot be compared,
           because the eye has to travel the width of the card between the two
           facts that differ. Two columns from the sm breakpoint up; a third
           tile wraps onto its own line, which is correct — A and B are the pair
           people actually weigh against each other.

           `pt-4` and not merely `pb-4`, which is what this was: CardHeader
           carries no bottom padding of its own, so the first row of tiles
           started flush under the "Add option" button beside the title, with no
           gap between the two at all. It is the same 4 CardBody gives every
           other card on these two screens. */
        <ul className="grid gap-3 px-[18px] py-4 sm:grid-cols-2">
          {options.map((option) => (
            <OptionTile
              key={option.id}
              option={option}
              record={record}
              readOnly={readOnly}
              pending={pending}
              confirming={confirmingSelect === option.id}
              onEdit={() => setEditing(option)}
              onSelect={(acknowledged) => handleSelect(option, acknowledged)}
              onCancelSelect={() => setConfirmingSelect(null)}
            />
          ))}

          {/* The empty slot, drawn rather than left blank. It is the same act as
              the header Add button and deliberately NOT worded the same: the
              header names what it does, this tile names what the thing it would
              create is FOR — a second column on the offer for the client to
              choose between. Two buttons sharing one name would also be two
              buttons a screen reader cannot tell apart. */}
          {!readOnly && options.length < MAX_OPTIONS ? (
            <li className="flex">
              <button
                type="button"
                onClick={() => setEditing('new')}
                className="border-border-default text-text-muted hover:border-brand hover:text-brand flex min-h-[76px] w-full items-center justify-center gap-2 rounded-[var(--radius-DEFAULT)] border border-dashed px-4 py-3 text-[13px] transition-colors"
              >
                <Plus size={15} aria-hidden />
                {t('quotes.routes.addNext', {
                  letter: nextOptionLetter(options),
                })}
              </button>
            </li>
          ) : null}
        </ul>
      )}

      <RouteOptionFormSheet
        open={editing !== null}
        onClose={() => setEditing(null)}
        record={editing === 'new' || editing === null ? undefined : editing}
        pending={pending}
        deleteError={
          optionHoldsCosting(remove.error)
            ? t('quotes.routes.deleteHoldsCosting')
            : remove.error
              ? t('quotes.routes.deleteFailed')
              : undefined
        }
        onSubmit={(payload) => {
          if (editing === 'new') {
            create.mutate(payload, { onSuccess: () => setEditing(null) })
          } else if (editing) {
            update.mutate(
              { optionId: editing.id, payload },
              { onSuccess: () => setEditing(null) },
            )
          }
        }}
        onDelete={
          editing !== 'new' && editing !== null
            ? () =>
                remove.mutate(editing.id, { onSuccess: () => setEditing(null) })
            : undefined
        }
      />
    </Card>
  )
}

/* One routing, as a tile. The letter is the handle the offer's columns are
 * cited by; the selected option carries the quote's own computed total, the
 * others their typed indicative figure — visibly approximate, because nobody
 * costed it. */
function OptionTile({
  option,
  record,
  readOnly,
  pending,
  confirming,
  onEdit,
  onSelect,
  onCancelSelect,
}: {
  option: RouteOption
  record: QuoteDetail
  readOnly: boolean
  pending: boolean
  confirming: boolean
  onEdit: () => void
  onSelect: (acknowledged: boolean) => void
  onCancelSelect: () => void
}) {
  const { t, i18n } = useTranslation()

  const mode = modeFromCode(option.transportModeCode)
  const ModeIcon = mode ? MODE_ICONS[mode] : undefined

  /* Origin and destination hold ISO 3166-1 alpha-2 once entered through the
     picker, so they are resolved to the localised country name for reading:
     "CN → Dostyk → TM" is a database row, not a route a salesperson recognises.
     A value that is not a code — a port from before the picker, or the
     importer's own text — passes through as written, the same carry-over rule
     CountryDropdown applies to the field itself.

     `placeName` is shared with the case header, the two list surfaces and the
     quote's own header, because every screen that prints a routing endpoint has
     the same two generations of value to read. It used to be a local helper
     here, and the case surfaces printed the raw column beside it. */
  const route = [
    placeName(option.origin, i18n.language),
    option.borderCrossing,
    placeName(option.destination, i18n.language),
  ]
    .filter(Boolean)
    .join(' → ')

  const transit =
    option.transitDaysMin != null && option.transitDaysMax != null
      ? option.transitDaysMin === option.transitDaysMax
        ? t('quotes.routes.transitDays', { count: option.transitDaysMin })
        : `${option.transitDaysMin}–${option.transitDaysMax} ${t('quotes.routes.daysShort')}`
      : option.transitDaysMax != null
        ? t('quotes.routes.transitUpTo', { count: option.transitDaysMax })
        : option.transitDaysMin != null
          ? t('quotes.routes.transitFrom', { count: option.transitDaysMin })
          : undefined

  const detail = [
    mode ? t(modeLabelKey(mode)) : undefined,
    transit,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <li
      className={cn(
        'flex flex-col gap-2.5 rounded-[var(--radius-DEFAULT)] border px-4 py-3',
        option.selected
          ? 'border-brand bg-surface shadow-[var(--focus-ring)]'
          : 'border-border-subtle',
      )}
    >
      <div className="flex items-start gap-3">
        {/* The selection, drawn as the shape a selection has. aria-hidden and
            purely decorative: the state is CARRIED by the Selected chip below,
            in words, and a lone ring would be the one thing on this card saying
            something with colour alone. */}
        <span
          aria-hidden
          className={cn(
            'mt-1 size-4 shrink-0 rounded-full border bg-white',
            option.selected
              ? 'border-brand border-[5px]'
              : 'border-border-default',
          )}
        />

        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex min-w-0 items-center gap-2">
            {/* The letter as a pill rather than as a coloured character in a
                column of its own. It is what the offer cites the routing by —
                option B is a thing said out loud on the phone — so it is set as
                an identifier, in the same inverse ink the record number uses. */}
            <span className="bg-ink-900 asa-num shrink-0 rounded-full px-2 py-0.5 text-[11px] leading-none font-semibold text-white">
              {optionLetter(option.position)}
            </span>
            <span className="text-text-heading truncate text-[13.5px]">
              {route}
            </span>
          </span>
          {detail || ModeIcon ? (
            <span className="text-text-muted flex items-center gap-1.5 text-[12px] font-light">
              {ModeIcon ? (
                <ModeIcon size={14} className="shrink-0" aria-hidden />
              ) : null}
              {detail}
            </span>
          ) : null}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/* The figure. The selected option's is the costing's own total —
            never a stored copy — and the indicative ones read as estimates
            because that is what a typed number on an uncosted routing is. */}
        {option.selected ? (
          record.costTotals && record.costTotals.sell.amount !== '0.00' ? (
            <span className="asa-num text-text-heading text-[13px] font-medium">
              {formatNumber(record.costTotals.sell.amount, 2)}
              <span className="text-text-faint ml-1 text-[11px] font-normal">
                {record.costTotals.sell.currency}
              </span>
            </span>
          ) : null
        ) : option.indicativeTotal ? (
          <span className="asa-num text-text-muted text-[13px]">
            ≈ {formatNumber(option.indicativeTotal.amount, 2)}
            <span className="text-text-faint ml-1 text-[11px]">
              {option.indicativeTotal.currency}
            </span>
          </span>
        ) : null}

        {option.selected ? (
          <span className="bg-brand-soft text-brand rounded-[var(--radius-sm)] px-1.5 py-0.5 text-[11px] font-medium">
            {t('quotes.routes.selected')}
          </span>
        ) : !readOnly ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={pending}
            onClick={() => onSelect(false)}
          >
            {t('quotes.routes.select')}
          </Button>
        ) : null}

        {!readOnly ? (
          <Button variant="ghost" size="sm" disabled={pending} onClick={onEdit}>
            {t('common.actions.edit')}
          </Button>
        ) : null}
      </div>

      {/* The FR-3 repricing warning, against the option it would promote. The
          server refused the silent move; this strip is the question it asked. */}
      {confirming ? (
        <div className="bg-attention-soft flex flex-wrap items-center gap-2 rounded-[var(--radius-sm)] px-3 py-2">
          <span className="text-text-body flex-1 text-[12.5px] leading-snug">
            {t('quotes.routes.selectWarning')}
          </span>
          <Button variant="secondary" size="sm" disabled={pending} onClick={onCancelSelect}>
            {t('common.actions.cancel')}
          </Button>
          <Button variant="primary" size="sm" disabled={pending} onClick={() => onSelect(true)}>
            {t('quotes.routes.selectConfirm')}
          </Button>
        </div>
      ) : null}
    </li>
  )
}

interface OptionDraft {
  origin: string
  borderCrossing: string
  destination: string
  transportModeId: string
  transitDaysMin: string
  transitDaysMax: string
  indicativeTotal: string
}

const emptyOption: OptionDraft = {
  origin: '',
  borderCrossing: '',
  destination: '',
  transportModeId: '',
  transitDaysMin: '',
  transitDaysMax: '',
  indicativeTotal: '',
}

function isBlankOrCount(raw: string): boolean {
  if (raw.trim() === '') return true
  const n = Number(raw)
  return Number.isInteger(n) && n > 0
}

/* One sheet for add and edit, like every record sheet on this surface. The
 * delete affordance sits at the FOOT for CargoFormSheet's reason, and its
 * refusal — the selected option holds the costing (FR-3) — is reported right
 * there, because "select another option first" is an instruction about THIS
 * form's subject, not a page-level failure. */
function RouteOptionFormSheet({
  open,
  onClose,
  record,
  pending,
  deleteError,
  onSubmit,
  onDelete,
}: {
  open: boolean
  onClose: () => void
  record?: RouteOption
  pending?: boolean
  deleteError?: string
  onSubmit: (payload: RouteOptionPayload) => void
  onDelete?: () => void
}) {
  const { t } = useTranslation()
  const { data: modes } = useTransportRefs('mode', true)

  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const [draft, setDraft, touched, markTouched] = useFormDraft<OptionDraft>(
    open ? (record?.id ?? 'new') : null,
    () =>
      record
        ? {
            origin: record.origin,
            borderCrossing: record.borderCrossing ?? '',
            destination: record.destination,
            transportModeId: record.transportModeId ?? '',
            transitDaysMin: record.transitDaysMin?.toString() ?? '',
            transitDaysMax: record.transitDaysMax?.toString() ?? '',
            indicativeTotal: record.indicativeTotal?.amount ?? '',
          }
        : emptyOption,
  )

  const originError =
    touched && draft.origin.trim() === '' ? t('common.form.required') : undefined
  const destinationError =
    touched && draft.destination.trim() === '' ? t('common.form.required') : undefined

  const transitInvalid =
    !isBlankOrCount(draft.transitDaysMin) || !isBlankOrCount(draft.transitDaysMax)
  const transitInverted =
    draft.transitDaysMin !== '' &&
    draft.transitDaysMax !== '' &&
    Number(draft.transitDaysMin) > Number(draft.transitDaysMax)
  const transitError =
    touched && transitInvalid
      ? t('common.form.invalidNumber')
      : touched && transitInverted
        ? t('quotes.form.transitInverted')
        : undefined

  const indicativeInvalid =
    draft.indicativeTotal.trim() !== '' &&
    (!Number.isFinite(Number(draft.indicativeTotal.replace(',', '.'))) ||
      Number(draft.indicativeTotal.replace(',', '.')) <= 0)
  const indicativeError =
    touched && indicativeInvalid ? t('common.form.invalidNumber') : undefined

  const invalid =
    draft.origin.trim() === '' ||
    draft.destination.trim() === '' ||
    transitInvalid ||
    transitInverted ||
    indicativeInvalid

  const count = (raw: string): number | undefined =>
    raw.trim() === '' ? undefined : Number(raw)

  return (
    <FormSheet
      open={open}
      onClose={() => {
        setConfirmingDelete(false)
        onClose()
      }}
      title={record ? t('quotes.routes.edit') : t('quotes.routes.add')}
      pending={pending}
      error={
        touched && invalid ? t('common.form.fixFields') : deleteError
      }
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit({
          origin: draft.origin.trim(),
          borderCrossing: draft.borderCrossing.trim() || undefined,
          destination: draft.destination.trim(),
          transportModeId: draft.transportModeId || undefined,
          transitDaysMin: count(draft.transitDaysMin),
          transitDaysMax: count(draft.transitDaysMax),
          // Comma decimals normalised the way toCargoPayload does: that is
          // what a Russian keyboard produces (AD-9 keeps it a string).
          indicativeTotal:
            draft.indicativeTotal.trim().replace(',', '.') || undefined,
        })
      }}
    >
      {/* The FR-19 structure: three fields, never one string, so conversion
          reads POL → border → POD without parsing what somebody typed.

          Origin and destination are ISO 3166-1 COUNTRY pickers; the border
          crossing stays free text, because a crossing is a named point —
          Dostyk, Sarakhs — and no code list enumerates them.

          What this costs, recorded here because the field's meaning changed and
          migration 0020 still describes these columns as POL/POD: an option
          entered through this form names a COUNTRY, not a port. Rows saved
          before it still hold their port and still render — CountryDropdown
          carries an unrecognised value through rather than blanking it — so the
          two generations coexist. Capturing the port again means a second field
          beside this one, not a change here. */}
      <Field label={t('quotes.routes.origin')} required error={originError}>
        {(props) => (
          <CountryDropdown
            value={draft.origin}
            onChange={(origin) => setDraft({ ...draft, origin })}
            {...props}
          />
        )}
      </Field>

      <Field
        label={t('quotes.routes.borderCrossing')}
        hint={t('common.form.optional')}
      >
        {(props) => (
          <Input
            value={draft.borderCrossing}
            onChange={(e) => setDraft({ ...draft, borderCrossing: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <Field label={t('quotes.routes.destination')} required error={destinationError}>
        {(props) => (
          <CountryDropdown
            value={draft.destination}
            onChange={(destination) => setDraft({ ...draft, destination })}
            {...props}
          />
        )}
      </Field>

      <Field label={t('quotes.routes.mode')} hint={t('common.form.optional')}>
        {(props) => (
          <Select
            value={draft.transportModeId}
            onChange={(transportModeId) => setDraft({ ...draft, transportModeId })}
            options={(modes ?? []).map((mode) => ({
              value: mode.id,
              label: mode.label,
            }))}
            placeholder={t('common.form.selectPlaceholder')}
            {...props}
          />
        )}
      </Field>

      <Field
        label={t('quotes.form.transitDays')}
        hint={t('common.form.optional')}
        error={transitError}
      >
        {(props) => (
          <div className="flex items-center gap-2">
            <Input
              inputMode="numeric"
              value={draft.transitDaysMin}
              onChange={(e) => setDraft({ ...draft, transitDaysMin: e.target.value })}
              aria-label={t('quotes.form.transitDaysMin')}
              className="asa-num"
              {...props}
            />
            <span className="text-text-faint">–</span>
            <Input
              inputMode="numeric"
              value={draft.transitDaysMax}
              onChange={(e) => setDraft({ ...draft, transitDaysMax: e.target.value })}
              aria-label={t('quotes.form.transitDaysMax')}
              className="asa-num"
            />
          </div>
        )}
      </Field>

      {/* The typed figure a NON-selected option shows (FR-3). The selected
          option's total comes off its cost lines, so entering one here for it
          would be the stored copy that goes stale — the hint says so. */}
      <Field
        label={t('quotes.routes.indicativeTotal')}
        hint={t('quotes.routes.indicativeHint')}
        error={indicativeError}
      >
        {(props) => (
          <Input
            inputMode="decimal"
            value={draft.indicativeTotal}
            onChange={(e) => setDraft({ ...draft, indicativeTotal: e.target.value })}
            className="asa-num"
            {...props}
          />
        )}
      </Field>

      {onDelete ? (
        <div className="border-line-hair mt-1 border-t pt-3">
          {confirmingDelete ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-text-body flex-1 text-[12.5px] leading-snug">
                {t('quotes.routes.deleteConfirm')}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setConfirmingDelete(false)}
                disabled={pending}
              >
                {t('common.actions.cancel')}
              </Button>
              <Button variant="danger" size="sm" onClick={onDelete} disabled={pending}>
                {t('quotes.routes.deleteConfirmAction')}
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              disabled={pending}
              className="text-negative cursor-pointer text-[12.5px] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('quotes.routes.delete')}
            </button>
          )}
        </div>
      ) : null}
    </FormSheet>
  )
}
